import React from 'react';
import { X } from 'lucide-react';
import { UploaderType } from 'src/shared/types';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from 'src/shared/components/ui/dialog';
import CMSArticle from 'src/shared/components/cms-article/CMSArticle';
import Loader from 'src/shared/components/loader/Loader';
import fileApiService from 'src/services/file-api-service';
import { FileRow } from './FilesTable';

type ViewImageDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    imageData: FileRow;
    projectId: string;
    imageAlt?: string;
};

const ViewImageDialog = ({ isOpen, onClose, imageData, projectId, imageAlt = 'Image preview' }: ViewImageDialogProps) => {
    const [imageBlobUrl, setImageBlobUrl] = React.useState<string | null>(null);
    const [isImageLoading, setIsImageLoading] = React.useState(false);
    const [imageLoadError, setImageLoadError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!isOpen || !projectId || !imageData?.id) {
            return;
        }

        let isActive = true;
        let objectUrl: string | null = null;

        const loadImage = async () => {
            setIsImageLoading(true);
            setImageLoadError(null);

            try {
                const imageBlob = await fileApiService.getImageForSiteFilesPreview({ projectId, fileId: imageData.id });
                objectUrl = URL.createObjectURL(imageBlob);

                if (isActive) {
                    setImageBlobUrl(prev => {
                        if (prev) {
                            URL.revokeObjectURL(prev);
                        }
                        return objectUrl;
                    });
                } else if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                }
            } catch (_error) {
                if (isActive) {
                    setImageLoadError('Failed to load image preview!');
                    setImageBlobUrl(prev => {
                        if (prev) {
                            URL.revokeObjectURL(prev);
                        }
                        return null;
                    });
                }
            } finally {
                if (isActive) {
                    setIsImageLoading(false);
                }
            }
        };

        loadImage();

        return () => {
            isActive = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [isOpen, projectId, imageData?.id]);

    if (!imageData) {
        return null;
    }

    // check NM internal uploads by looking at uploadedBy and awsFileKey properties
    const isUploadedByNM = imageData.uploadedBy === UploaderType.NatureMetrics && !imageData.awsFileKey?.includes('public/user_uploads');

    // check image name to determine internal metacoder,  bubble plot species chart, and bubble plot order chart images
    const isMetacoderImage = /- Taxonomic Composition.png$/i.test(imageData.name);
    const isBubblePlotSpeciesImage = /- Community Composition - species.png$/i.test(imageData.name);
    const isBubblePlotOrderImage = /- Community Composition - (?!species\.png$).+\.png$/i.test(imageData.name);

    const showInternalImageDescription = isUploadedByNM && (isMetacoderImage || isBubblePlotSpeciesImage || isBubblePlotOrderImage);

    return (
        <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
            <DialogContent
                className='flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border-none bg-white p-4'
                overlayClassName='bg-black/50'
                hideCloseButton
            >
                <DialogTitle className='sr-only'>{imageAlt}</DialogTitle>
                <div className='flex shrink-0 justify-end'>
                    <DialogClose className='opacity-70 transition-opacity hover:opacity-100'>
                        <X className='h-4 w-4' />
                        <span className='sr-only'>Close</span>
                    </DialogClose>
                </div>
                <div className='flex min-h-[260px] flex-1 items-center justify-center overflow-hidden rounded bg-slate-50 sm:min-h-[320px]'>
                    {isImageLoading && <Loader />}
                    {!isImageLoading && imageLoadError && <div className='text-sm p-4 bg-grey-0 rounded-xl'>{imageLoadError}</div>}
                    {!isImageLoading && !imageLoadError && imageBlobUrl && (
                        <img
                            src={imageBlobUrl}
                            alt={imageAlt}
                            className={`max-w-full object-contain rounded ${
                                showInternalImageDescription ? 'max-h-[55vh]' : 'max-h-[75vh]'
                            }`}
                        />
                    )}
                </div>
                {showInternalImageDescription && (
                    <DialogDescription asChild>
                        <div className='max-h-80 shrink-0 overflow-auto p-4 text-sm text-muted-foreground rounded-xl bg-grey-0'>
                            <div className='text-black'>
                                <div className='text-xl mb-3'>{imageData.name}</div>
                                <div className='max-h-[125px] overflow-auto'>
                                    {isMetacoderImage && <CMSArticle slug='metacoder-chart-image-description' />}
                                    {isBubblePlotSpeciesImage && <CMSArticle slug='bubble-chart-species-image-description' />}
                                    {isBubblePlotOrderImage && <CMSArticle slug='bubble-chart-order-image-description' />}
                                </div>
                            </div>
                        </div>
                    </DialogDescription>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ViewImageDialog;
